import { useAuth } from '@/hooks/useAuth';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/firebase/config';
import {
  Loader2, Server, Users, Trash2, Ban, ShieldCheck, ShieldAlert, Activity, Lock, Clock,
  Gauge, Signal, Gamepad2, CheckCircle2, BadgeCheck, MapPin, ExternalLink, Wrench,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

// Seguridad de los sistemas de pasos de Roblox (repo `sistemasroblox`): cada servidor de
// Roblox que los ejecuta manda un latido cada 30 s a `robloxHeartbeat` → un documento por
// place en `roblox_places`. Desde aquí se ve en qué places corren y se bloquean.
const setAuthorizedFn = httpsCallable(functions, 'robloxSecurity_setAuthorized');
const setApprovedFn = httpsCallable(functions, 'robloxSecurity_setApproved');
const deleteEntryFn = httpsCallable(functions, 'robloxSecurity_deleteEntry');

// Un place está "en marcha" si latió hace menos de 90 s (el latido va cada 30 s).
const ONLINE_THRESHOLD_MS = 90 * 1000;

type RobloxServer = {
  lastSeen?: number;
  startedAt?: number;
  players?: number;
  privateServer?: boolean;
  studio?: boolean;
  ip?: string;
};

type RobloxPlace = {
  id: string;
  placeId?: string;
  gameId?: string;
  gameName?: string;
  creatorId?: string;
  creatorType?: string;
  creatorName?: string;
  placeVersion?: number;
  systemVersion?: string;
  pasos?: string[];
  cortejos?: number;
  studio?: boolean;
  lastIp?: string;
  lastSeen?: any;
  firstSeen?: any;
  servers?: Record<string, RobloxServer>;
  playersNow?: number;
  days?: Record<string, number>;
  authorized?: boolean;
  approved?: boolean;
  killTitle?: string;
  killMessage?: string;
};

type GroupKey = 'pending' | 'approved' | 'lockdown';

const GROUPS: { key: GroupKey; title: string; hint: string; tone: 'rose' | 'amber' | 'emerald'; icon: React.ReactNode }[] = [
  {
    key: 'pending', title: 'Sin aprobar',
    hint: 'Places que no son nuestros (o aún no se han marcado): mira quién es el dueño antes de dejarlo.',
    tone: 'amber', icon: <ShieldAlert className="h-4 w-4" />,
  },
  {
    key: 'lockdown', title: 'Bloqueados',
    hint: 'El sistema está apagado en ellos y los jugadores ven el mensaje.',
    tone: 'rose', icon: <Lock className="h-4 w-4" />,
  },
  {
    key: 'approved', title: 'Aprobados',
    hint: 'Places autorizados: el de pruebas, el del cliente…',
    tone: 'emerald', icon: <CheckCircle2 className="h-4 w-4" />,
  },
];

const TONES = {
  rose: { text: 'text-rose-300', bg: 'bg-rose-500/15', border: 'border-rose-400/30' },
  amber: { text: 'text-amber-300', bg: 'bg-amber-400/15', border: 'border-amber-400/30' },
  emerald: { text: 'text-emerald-300', bg: 'bg-emerald-500/15', border: 'border-emerald-400/30' },
};

function toMs(ts: any): number | null {
  if (!ts) return null;
  if (typeof ts === 'number') return ts;
  if (typeof ts.toDate === 'function') return ts.toDate().getTime();
  if (ts.seconds) return ts.seconds * 1000;
  return null;
}

function fmt(ts: any): string {
  const ms = toMs(ts);
  if (!ms) return '——';
  return new Date(ms).toLocaleString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function relativeFrom(ms: number | null, now: number): string {
  if (!ms) return '——';
  const s = Math.floor(Math.max(0, now - ms) / 1000);
  if (s < 60) return `hace ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h} h`;
  return `hace ${Math.floor(h / 24)} d`;
}

function madridDayKey(d: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d);
}

function DataRow({ label, value, icon }: { label: string; value: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {icon && <span className="text-indigo-400/70">{icon}</span>}
      <span className="shrink-0 text-muted-foreground/70">{label}</span>
      <span className="h-px flex-1 bg-white/8" />
      <span className="truncate font-medium text-foreground/90">{value}</span>
    </div>
  );
}

export default function AdminSeguridadRobloxPage() {
  const { user, userData, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [places, setPlaces] = useState<RobloxPlace[]>([]);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [killTarget, setKillTarget] = useState<RobloxPlace | null>(null);
  const [killTitle, setKillTitle] = useState('SISTEMA NO AUTORIZADO');
  const [killMessage, setKillMessage] = useState(
    'Este juego no tiene licencia para usar el sistema cofrade. Contacta con el autor.'
  );

  useEffect(() => {
    if (!loading && userData?.role !== 'admin') navigate('/jartinet', { replace: true });
  }, [user, userData, loading, navigate]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (userData?.role !== 'admin') return;
    return onSnapshot(query(collection(db, 'roblox_places')), (snap) => {
      setPlaces(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
    });
  }, [userData?.role]);

  const isOnline = (p: RobloxPlace) => {
    const ms = toMs(p.lastSeen);
    return ms != null && now - ms < ONLINE_THRESHOLD_MS;
  };

  // Servidores vivos de un place (los que latieron hace < 90 s).
  const liveServers = (p: RobloxPlace) =>
    Object.entries(p.servers || {})
      .filter(([, s]) => s.lastSeen && now - s.lastSeen < ONLINE_THRESHOLD_MS)
      .sort((a, b) => (b[1].players || 0) - (a[1].players || 0));

  const nowBucket = Math.floor(now / 5000);
  const groups = useMemo(() => {
    const g: Record<GroupKey, RobloxPlace[]> = { pending: [], approved: [], lockdown: [] };
    places.forEach((p) => {
      if (p.authorized === false) g.lockdown.push(p);
      else if (p.approved) g.approved.push(p);
      else g.pending.push(p);
    });
    const order = (a: RobloxPlace, b: RobloxPlace) => {
      const oa = isOnline(a) ? 0 : 1;
      const ob = isOnline(b) ? 0 : 1;
      if (oa !== ob) return oa - ob;
      return (toMs(b.lastSeen) || 0) - (toMs(a.lastSeen) || 0);
    };
    (Object.keys(g) as GroupKey[]).forEach((k) => g[k].sort(order));
    return g;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [places, nowBucket]);

  async function setApproved(p: RobloxPlace, approved: boolean) {
    setBusy(true);
    try {
      await setApprovedFn({ placeId: p.id, approved });
      toast({ title: approved ? 'Place aprobado' : 'Aprobación retirada', description: p.gameName || p.id });
    } catch (e: any) {
      toast({ title: 'Error', description: e?.message || 'No se pudo.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }

  function openKill(p: RobloxPlace) {
    setKillTarget(p);
    setKillTitle(p.killTitle || 'SISTEMA NO AUTORIZADO');
    setKillMessage(p.killMessage || 'Este juego no tiene licencia para usar el sistema cofrade. Contacta con el autor.');
  }

  async function applyKill() {
    if (!killTarget) return;
    setBusy(true);
    try {
      await setAuthorizedFn({ placeId: killTarget.id, authorized: false, killTitle, killMessage });
      toast({ title: 'Place bloqueado', description: 'El sistema se apagará en sus servidores en ≈30 s.' });
      setKillTarget(null);
    } catch (e: any) {
      toast({ title: 'Error', description: e?.message || 'No se pudo.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }

  async function restore(p: RobloxPlace) {
    setBusy(true);
    try {
      await setAuthorizedFn({ placeId: p.id, authorized: true });
      toast({ title: 'Acceso restaurado', description: 'El sistema vuelve en ≈30 s.' });
    } catch (e: any) {
      toast({ title: 'Error', description: e?.message || 'No se pudo.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }

  async function remove(p: RobloxPlace) {
    if (!confirm('¿Eliminar este registro? Reaparecerá si el place sigue ejecutando el sistema.')) return;
    try {
      await deleteEntryFn({ placeId: p.id });
      toast({ title: 'Registro eliminado' });
    } catch (e: any) {
      toast({ title: 'Error', description: e?.message || 'No se pudo eliminar.', variant: 'destructive' });
    }
  }

  if (loading || userData?.role !== 'admin') {
    return (
      <div className="flex h-[calc(100vh-10rem)] w-full flex-col items-center justify-center gap-3">
        <ShieldCheck className="h-12 w-12 animate-pulse text-indigo-400" />
        <p className="text-sm text-muted-foreground">Verificando permisos…</p>
      </div>
    );
  }

  const online = places.filter(isOnline);
  const serversNow = online.reduce((s, p) => s + liveServers(p).length, 0);
  const playersNow = online.reduce((s, p) => s + liveServers(p).reduce((a, [, x]) => a + (x.players || 0), 0), 0);
  const today = madridDayKey(new Date(now));

  return (
    <div className="space-y-6 pb-12">
      {/* ── Cabecera ── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-indigo-500/15 via-card/40 to-card/20 p-8"
      >
        <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-28 left-1/3 h-64 w-64 rounded-full bg-sky-500/10 blur-3xl" />
        <div className="relative flex flex-wrap items-center justify-between gap-5">
          <div className="flex items-center gap-5">
            <div className="relative">
              <div className="absolute inset-0 rounded-2xl bg-indigo-500/40 blur-xl" />
              <div className="relative grid h-16 w-16 place-items-center rounded-2xl border border-indigo-400/30 bg-indigo-500/15 text-indigo-300">
                <Gamepad2 className="h-9 w-9" />
              </div>
            </div>
            <div>
              <h1 className="font-headline text-4xl font-bold">Seguridad de Roblox</h1>
              <p className="mt-2 max-w-2xl text-muted-foreground">
                En qué juegos de Roblox se están ejecutando los sistemas de pasos, quién es su dueño y cuánta gente hay.
                Desde aquí se bloquea un juego.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-4 py-2 text-xs font-medium text-emerald-300">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            En directo · {new Date(now).toLocaleTimeString('es-ES')}
          </div>
        </div>
      </motion.div>

      {/* ── Métricas ── */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile icon={<Activity className="h-5 w-5" />} label="Juegos en marcha" value={`${online.length}/${places.length}`} accent="emerald" />
        <StatTile icon={<Server className="h-5 w-5" />} label="Servidores ahora" value={serversNow} accent="indigo" />
        <StatTile icon={<Users className="h-5 w-5" />} label="Jugadores ahora" value={playersNow} accent="sky" />
        <StatTile
          icon={<ShieldAlert className="h-5 w-5" />} label="Sin aprobar · bloqueados"
          value={`${groups.pending.length} · ${groups.lockdown.length}`}
          accent={groups.pending.length ? 'amber' : groups.lockdown.length ? 'rose' : 'slate'}
        />
      </div>

      {places.length === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-white/10 py-16 text-muted-foreground">
          <Gamepad2 className="h-10 w-10" />
          <p className="text-sm">Ningún juego de Roblox ha reportado todavía.</p>
        </div>
      )}

      {GROUPS.map((g) => groups[g.key].length > 0 && (
        <section key={g.key} className="space-y-3">
          <div className="flex items-center gap-2">
            <span className={cn('grid h-7 w-7 place-items-center rounded-lg border', TONES[g.tone].bg, TONES[g.tone].border, TONES[g.tone].text)}>
              {g.icon}
            </span>
            <h2 className="text-lg font-bold">{g.title}</h2>
            <span className="rounded-full bg-white/10 px-2 text-xs">{groups[g.key].length}</span>
            <span className="text-xs text-muted-foreground">{g.hint}</span>
          </div>
          <div className="grid gap-3 xl:grid-cols-2">
            {groups[g.key].map((p) => {
              const on = isOnline(p);
              const locked = p.authorized === false;
              const vivos = liveServers(p);
              const jugadores = vivos.reduce((a, [, x]) => a + (x.players || 0), 0);
              const dueno = p.creatorName
                ? `${p.creatorName} (${p.creatorType === 'Group' ? 'grupo' : 'usuario'} ${p.creatorId})`
                : p.creatorId || '——';
              return (
                <div
                  key={p.id}
                  className={cn(
                    'relative overflow-hidden rounded-2xl border bg-card/40 p-5',
                    locked ? 'border-rose-500/50 shadow-[0_0_24px_-6px_rgba(244,63,94,0.45)]'
                      : !p.approved ? 'border-amber-400/40' : 'border-white/10',
                  )}
                >
                  <span className={cn('absolute inset-y-0 left-0 w-1', locked ? 'bg-rose-500' : on ? 'bg-emerald-400' : 'bg-white/10')} />
                  <div className="space-y-3 pl-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="grid h-9 w-9 place-items-center rounded-xl border border-indigo-400/30 bg-indigo-500/15 text-indigo-300">
                        <Gamepad2 className="h-4 w-4" />
                      </div>
                      <a
                        href={`https://www.roblox.com/games/${p.placeId || p.id}`}
                        target="_blank" rel="noreferrer"
                        className="flex items-center gap-1 truncate text-base font-bold hover:underline"
                      >
                        {p.gameName || `Place ${p.id}`} <ExternalLink className="h-3.5 w-3.5 opacity-60" />
                      </a>
                      <StatusPill online={on} />
                      {locked && <Chip tone="rose" icon={<Lock className="h-3 w-3" />}>Bloqueado</Chip>}
                      {!p.approved && !locked && <Chip tone="amber" icon={<ShieldAlert className="h-3 w-3" />}>Sin aprobar</Chip>}
                      {p.approved && <Chip tone="emerald" icon={<BadgeCheck className="h-3 w-3" />}>Aprobado</Chip>}
                      {p.studio && <Chip tone="sky" icon={<Wrench className="h-3 w-3" />}>Studio</Chip>}
                    </div>

                    <div className="grid gap-2 sm:grid-cols-2">
                      <DataRow icon={<Users className="h-3.5 w-3.5" />} label="Dueño" value={dueno} />
                      <DataRow label="PlaceId · GameId" value={`${p.placeId || p.id} · ${p.gameId || '——'}`} />
                      <DataRow icon={<Server className="h-3.5 w-3.5" />} label="Servidores ahora" value={vivos.length} />
                      <DataRow icon={<Users className="h-3.5 w-3.5" />} label="Jugadores ahora" value={`${jugadores} (máx. hoy ${p.days?.[today] ?? 0})`} />
                      <DataRow icon={<Gauge className="h-3.5 w-3.5" />} label="Sistema · versión del place" value={`${p.systemVersion || '——'} · v${p.placeVersion ?? '?'}`} />
                      <DataRow icon={<MapPin className="h-3.5 w-3.5" />} label="IP del servidor" value={p.lastIp || '——'} />
                      <DataRow icon={<Clock className="h-3.5 w-3.5" />} label="Visto por primera vez" value={fmt(p.firstSeen)} />
                      <DataRow icon={<Signal className="h-3.5 w-3.5" />} label="Último latido" value={relativeFrom(toMs(p.lastSeen), now)} />
                    </div>

                    {!!p.pasos?.length && (
                      <div className="flex flex-wrap gap-1.5">
                        {p.pasos.map((n, j) => (
                          <span key={j} className="rounded-full border border-white/10 bg-white/5 px-2.5 py-0.5 text-xs">{n}</span>
                        ))}
                        {!!p.cortejos && (
                          <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-0.5 text-xs">
                            {p.cortejos} cortejo{p.cortejos === 1 ? '' : 's'}
                          </span>
                        )}
                      </div>
                    )}

                    {vivos.length > 0 && (
                      <div className="space-y-1 rounded-xl border border-white/8 bg-white/[0.03] p-2.5">
                        {vivos.map(([job, s]) => (
                          <div key={job} className="flex items-center gap-2 text-[0.7rem] text-muted-foreground">
                            <Server className="h-3 w-3 text-indigo-400/70" />
                            <span className="font-mono opacity-70">{job.slice(0, 13)}</span>
                            <span className="h-px flex-1 bg-white/8" />
                            {s.privateServer && <span className="text-sky-300">privado</span>}
                            {s.studio && <span className="text-sky-300">Studio</span>}
                            <span>{s.players ?? 0} jug.</span>
                            <span>desde {relativeFrom(s.startedAt || null, now).replace('hace ', '')}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {locked && p.killMessage && (
                      <p className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-2.5 text-xs text-rose-200">
                        <strong>{p.killTitle}</strong> — {p.killMessage}
                      </p>
                    )}

                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <Button size="sm" variant="outline" disabled={busy} className="gap-1.5 rounded-xl border-white/15"
                        onClick={() => setApproved(p, !p.approved)}>
                        <BadgeCheck className="h-3.5 w-3.5" /> {p.approved ? 'Quitar aprobación' : 'Aprobar'}
                      </Button>
                      {locked ? (
                        <Button size="sm" disabled={busy} className="gap-1.5 bg-emerald-600 hover:bg-emerald-600/85" onClick={() => restore(p)}>
                          <ShieldCheck className="h-4 w-4" /> Restaurar acceso
                        </Button>
                      ) : (
                        <Button size="sm" variant="destructive" className="gap-1.5" onClick={() => openKill(p)}>
                          <Ban className="h-4 w-4" /> Bloquear
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-destructive" onClick={() => remove(p)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}

      {/* ── Diálogo de bloqueo ── */}
      <Dialog open={!!killTarget} onOpenChange={(o) => !o && setKillTarget(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Ban className="h-5 w-5" /> Bloquear el sistema en este juego
            </DialogTitle>
            <DialogDescription>
              Juego: <strong className="text-foreground">{killTarget?.gameName || killTarget?.id}</strong>. Los pasos se
              sueltan, no se pueden volver a coger y todos los jugadores ven el mensaje. Llega en ≈30 s.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="rbxKillTitle">Encabezado</Label>
              <Input id="rbxKillTitle" value={killTitle} onChange={(e) => setKillTitle(e.target.value)} maxLength={80} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rbxKillMessage">Mensaje</Label>
              <Textarea id="rbxKillMessage" value={killMessage} rows={3} maxLength={300}
                onChange={(e) => setKillMessage(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKillTarget(null)} disabled={busy}>Cancelar</Button>
            <Button variant="destructive" disabled={busy} onClick={applyKill} className="gap-1.5">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
              Bloquear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Piezas de UI (como en Seguridad del Mod) ──

const ACCENTS: Record<string, { text: string; bg: string; border: string }> = {
  emerald: { text: 'text-emerald-300', bg: 'bg-emerald-500/15', border: 'border-emerald-400/30' },
  indigo: { text: 'text-indigo-300', bg: 'bg-indigo-500/15', border: 'border-indigo-400/30' },
  sky: { text: 'text-sky-300', bg: 'bg-sky-500/15', border: 'border-sky-400/30' },
  amber: { text: 'text-amber-300', bg: 'bg-amber-400/15', border: 'border-amber-400/30' },
  rose: { text: 'text-rose-300', bg: 'bg-rose-500/15', border: 'border-rose-400/30' },
  slate: { text: 'text-muted-foreground', bg: 'bg-white/5', border: 'border-white/10' },
};

function StatTile({ icon, label, value, accent }: { icon: React.ReactNode; label: string; value: React.ReactNode; accent: string }) {
  const a = ACCENTS[accent] || ACCENTS.slate;
  return (
    <div className="rounded-2xl border border-white/10 bg-card/40 p-4">
      <div className="flex items-center gap-2.5">
        <span className={cn('grid h-9 w-9 place-items-center rounded-xl border', a.bg, a.border, a.text)}>{icon}</span>
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
      </div>
      <p className={cn('mt-2 text-3xl font-bold', a.text)}>{value}</p>
    </div>
  );
}

function StatusPill({ online }: { online: boolean }) {
  return (
    <span className={cn(
      'flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[0.65rem] font-semibold',
      online ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300' : 'border-white/10 bg-white/5 text-muted-foreground',
    )}>
      <span className={cn('h-1.5 w-1.5 rounded-full', online ? 'animate-pulse bg-emerald-400' : 'bg-muted-foreground/50')} />
      {online ? 'En marcha' : 'Sin señal'}
    </span>
  );
}

function Chip({ children, tone, icon }: { children: React.ReactNode; tone: 'rose' | 'amber' | 'emerald' | 'sky'; icon?: React.ReactNode }) {
  const t = {
    rose: 'border-rose-500/50 bg-rose-500/15 text-rose-300',
    amber: 'border-amber-400/50 bg-amber-400/15 text-amber-300',
    emerald: 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300',
    sky: 'border-sky-400/40 bg-sky-500/10 text-sky-300',
  }[tone];
  return (
    <span className={cn('flex items-center gap-1 rounded-full border px-2 py-0.5 text-[0.65rem] font-semibold', t)}>
      {icon} {children}
    </span>
  );
}
